/**
 * Horizontal lateral tabs with keyboard support and mobile scroll.
 * Tab buttons are built from tabpanel data attributes (source of truth for labels).
 */
class JvLateralTabs extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const { signal } = this.#controller;
    const nav = this.querySelector('[data-jv-lateral-tabs-nav]');
    const panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!nav || !panels.length) {
      if (nav) nav.hidden = true;
      return;
    }

    nav.hidden = false;
    this.#buildNav(nav, panels);

    const tabs = Array.from(nav.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    const activateTab = (targetTab, { scroll = false } = {}) => {
      const panelId = targetTab.getAttribute('aria-controls');
      const targetPanel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;

      for (const tab of tabs) {
        const isActive = tab === targetTab;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      }

      for (const panel of panels) {
        panel.classList.toggle('active', panel === targetPanel);
      }

      if (scroll) {
        targetTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    };

    for (const tab of tabs) {
      tab.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          activateTab(tab, { scroll: true });
        },
        { signal }
      );

      tab.addEventListener(
        'keydown',
        (event) => {
          const index = tabs.indexOf(tab);
          let nextTab = null;

          switch (event.key) {
            case 'ArrowLeft':
              event.preventDefault();
              nextTab = tabs[index - 1] ?? tabs[tabs.length - 1];
              break;
            case 'ArrowRight':
              event.preventDefault();
              nextTab = tabs[index + 1] ?? tabs[0];
              break;
            case 'Home':
              event.preventDefault();
              nextTab = tabs[0];
              break;
            case 'End':
              event.preventDefault();
              nextTab = tabs[tabs.length - 1];
              break;
            default:
              return;
          }

          if (nextTab) {
            nextTab.focus();
            activateTab(nextTab, { scroll: true });
          }
        },
        { signal }
      );
    }

    const initialTab = tabs.find((tab) => tab.classList.contains('active')) ?? tabs[0];
    activateTab(initialTab);
    this.classList.add('is-initialized');
  }

  /**
   * @param {HTMLElement} nav
   * @param {HTMLElement[]} panels
   */
  #buildNav(nav, panels) {
    const activeTabId = nav.querySelector('[role="tab"].active')?.id;
    nav.replaceChildren();

    for (const [index, panel] of panels.entries()) {
      const panelId = panel.id;
      const tabId = panel.getAttribute('aria-labelledby') || `jv-lateral-tab-${panel.dataset.tabPanel || index}`;
      const label = panel.getAttribute('data-tab-label')?.trim() || 'Aba';
      const isActive = activeTabId ? tabId === activeTabId : index === 0;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `jv-lateral-tabs__tab${isActive ? ' active' : ''}`;
      button.setAttribute('role', 'tab');
      button.id = tabId;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('aria-controls', panelId);
      button.setAttribute('data-tab', panel.dataset.tabPanel || String(index));
      button.setAttribute('tabindex', isActive ? '0' : '-1');
      button.textContent = label;

      nav.appendChild(button);
    }
  }

  refresh() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.classList.remove('is-initialized');
  }
}

if (!customElements.get('jv-lateral-tabs')) {
  customElements.define('jv-lateral-tabs', JvLateralTabs);
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  const refreshLateralTabs = () => {
    requestAnimationFrame(() => {
      for (const tabs of document.querySelectorAll('jv-lateral-tabs')) {
        if (typeof tabs.refresh === 'function') {
          tabs.refresh();
        }
      }
    });
  };

  document.addEventListener('shopify:section:load', refreshLateralTabs);
  document.addEventListener('shopify:section:reorder', refreshLateralTabs);
  document.addEventListener('shopify:block:select', refreshLateralTabs);
}
